<template>
  <div>
    <!-- Identifier -->
    <v-sheet color="white" elevation="1" class="pa-3">
      <v-row no-gutters class="align-center">
        <v-col cols="2" sm="1">
          <!-- display: block -->
          <!-- The badge is not shown for alarms and exception events as the color of the icon will be changed instead -->
          <v-badge
            bordered
            overlap
            :color="hasAlert(individual) ? 'error' : 'warning'"
            icon="mdi-exclamation-thick"
            :value="
              !isAlarmOrException(individual) &&
              (hasAlert(individual) || hasWarning(individual))
            "
          >
            <v-btn
              fab
              small
              @click="onClick"
              :color="
                isAlarmOrException(individual) && hasAlert(individual)
                  ? 'error'
                  : 'secondary'
              "
            >
              <v-icon>{{ recTypeIcon }}</v-icon>
            </v-btn>
          </v-badge>
        </v-col>

        <v-col cols="10" sm="11">
          <div class="text-caption text-truncate">
            <iri-value :iri="individual.type"></iri-value>
          </div>
          <div class="text-body-1 text-truncate">
            <iri-value :iri="individual.id"></iri-value>
          </div>

          <!-- display: block -->
          <!-- TODO place in a second column aligned top with id -->
          <data-prop
            v-for="(property, index) of metaProperties"
            :key="property.name.value"
            :dataProperty="metaProperties[index]"
          ></data-prop>
        </v-col>
      </v-row>
    </v-sheet>

    <!-- Properties (Attributes) -->
    <v-sheet color="white" elevation="1" class="pa-3 mt-4">
      <!-- Edit menu -->
      <v-row v-if="edit">
        <v-col cols="10" sm="11">
          <v-btn-toggle dense>
            <v-menu open-on-hover bottom offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  tile
                  outlined
                  :disabled="noAvailableProps"
                  v-on="on"
                  v-bind="attrs"
                >
                  <v-icon left color="secondary">mdi-plus</v-icon>
                  Add Property
                </v-btn>
              </template>
              <!-- List of data property options -->
              <v-list dense>
                <v-list-item-group>
                  <template v-for="(entry, index) in addDataPropMenu">
                    <v-divider
                      v-if="entry.divider"
                      :key="'divider-' + index"
                    ></v-divider>
                    <v-list-item
                      v-else
                      :key="entry.label"
                      :disabled="entry.disabled"
                      @click="addProperty(entry.iri)"
                      ripple
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ entry.label }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list-item-group>
              </v-list>
            </v-menu>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <v-row no-gutters class="align-center">
        <!-- Icon -->
        <v-col cols="2" sm="1">
          <!-- display: block -->
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon color="secondary" v-on="on" v-bind="attrs">
                <v-icon>mdi-format-list-bulleted</v-icon>
              </v-btn>
            </template>
            <span>Properties</span>
          </v-tooltip>
        </v-col>

        <v-col cols="10" sm="11">
          <!-- display: block -->
          <data-prop
            v-for="(property, index) of dataProperties"
            :key="property.name.value"
            :dataProperty="dataProperties[index]"
            :edit="edit"
            @delete="onDeleteProp(property)"
          ></data-prop>

          <!-- If list of data properties is empty -->
          <div v-if="!dataProperties.length" class="text-caption">Empty</div>
        </v-col>
      </v-row>
    </v-sheet>

    <!-- Components -->
    <v-expansion-panels
      v-model="expandedComponentPanels"
      multiple
      flat
      class="mt-4 component-panels"
    >
      <v-expansion-panel
        v-for="component of individual.components"
        :key="component.id.value"
        class="component-panel"
      >
        <v-expansion-panel-header class="pa-3">
          <v-row no-gutters class="align-center">
            <v-col cols="2" sm="1">
              <v-badge
                bordered
                overlap
                :color="hasAlert(component) ? 'error' : 'warning'"
                icon="mdi-exclamation-thick"
                :value="
                  !isAlarmOrException(component) &&
                  (hasAlert(component) || hasWarning(component))
                "
              >
                <v-btn
                  icon
                  @click.stop=""
                  :color="
                    isAlarmOrException(component) && hasAlert(component)
                      ? 'error'
                      : 'secondary'
                  "
                >
                  <v-icon>{{ componentTypeIcon(component.type) }}</v-icon>
                </v-btn>
              </v-badge>
            </v-col>
            <v-col cols="10" sm="11" class="d-flex align-center">
              <div class="text-caption text-truncate">
                <iri-value :iri="component.type" humanize></iri-value>
              </div>
              <v-spacer></v-spacer>
              <span
                v-if="isComponentEmpty(component)"
                class="text-caption grey--text mr-2"
              >
                <v-icon x-small color="grey" class="mr-1"
                  >mdi-circle-off-outline</v-icon
                >
                empty
              </span>
            </v-col>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row no-gutters>
            <v-col cols="2" sm="1"></v-col>
            <v-col cols="10" sm="11">
              <div class="text-body-1 text-truncate mb-3">
                <iri-value :iri="component.id"></iri-value>
              </div>
              <v-divider class="mb-3"></v-divider>
              <data-properties
                :dataProperties="component.properties"
              ></data-properties>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Outgoing -->
    <v-sheet color="white" elevation="1" class="pa-3 mt-4">
      <!-- Edit menu -->
      <v-row v-if="edit">
        <v-col cols="10" sm="11">
          <v-btn-toggle dense>
            <v-btn
              text
              tile
              outlined
              @click="onAddRelationship"
              :disabled="false"
            >
              <v-icon left color="secondary">mdi-plus</v-icon>
              Add Relationship
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <v-row no-gutters class="align-center">
        <!-- Icon -->
        <v-col cols="2" sm="1">
          <!-- display: block -->
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                @click.stop=""
                color="secondary"
                v-on="on"
                v-bind="attrs"
              >
                <v-icon>mdi-export</v-icon>
              </v-btn>
            </template>
            <span>Outgoing Relationships</span>
          </v-tooltip>
        </v-col>

        <v-col cols="10" sm="11">
          <!-- display: block -->
          <object-prop
            v-for="property of objectProperties"
            :key="property.name.value"
            :objectProperty="property"
            :edit="edit"
            @delete="onDeleteRel(property, $event)"
          ></object-prop>
          <!-- If list of object properties is empty -->
          <div
            v-if="!objectProperties.length"
            class="d-flex align-center text-caption grey--text pr-8"
          >
            <v-spacer></v-spacer>
            <v-icon x-small color="grey" class="mr-1"
              >mdi-circle-off-outline</v-icon
            >
            empty
          </div>
        </v-col>
      </v-row>
    </v-sheet>

    <!-- Incoming -->
    <v-subheader>Incoming</v-subheader>

    <v-sheet color="white" elevation="1" class="pa-3 mt-4">
      <v-row no-gutters class="align-center">
        <v-col cols="2" sm="1">
          <v-btn icon @click.stop="" color="secondary"
            ><v-icon>mdi-import</v-icon></v-btn
          >
        </v-col>
        <v-col cols="10" sm="11">
          <object-prop
            v-for="property of inverseObjectProperties"
            :key="property.name.value"
            :objectProperty="property"
          ></object-prop>
          <!-- If list of inverse object properties is empty -->
          <div
            v-if="!inverseObjectProperties.length"
            class="d-flex align-center text-caption grey--text pr-8"
          >
            <v-spacer></v-spacer>
            <v-icon x-small color="grey" class="mr-1"
              >mdi-circle-off-outline</v-icon
            >
            empty
          </div>
        </v-col>
      </v-row>
    </v-sheet>

    <!-- Dialog for adding relationship (two-tab: type selection + individual search) -->
    <v-dialog
      v-model="showAdd"
      transition="dialog-top-transition"
      max-width="900"
    >
      <v-card style="height: 80vh; display: flex; flex-direction: column">
        <v-toolbar color="lime darken-2" dark style="flex: 0 0 auto">
          <v-toolbar-title>Add Relationship</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="showAdd = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-tabs v-model="addTab" color="lime darken-2" style="flex: 0 0 auto">
          <v-tab>Relationship Type</v-tab>
          <v-tab :disabled="!selectedConstraint">Find Individual</v-tab>
        </v-tabs>

        <v-tabs-items
          v-model="addTab"
          style="flex: 1 1 0; overflow-y: auto; min-height: 0"
        >
          <!-- Tab 1: Relationship Type (instant) -->
          <v-tab-item>
            <div class="pa-4 pb-2">
              <div class="text-caption grey--text">
                Select a relationship type for
                <strong
                  ><iri-value
                    :iri="individual.type"
                    humanize
                  ></iri-value></strong
                >. <v-icon x-small>mdi-export</v-icon> outgoing
                <v-icon x-small>mdi-import</v-icon> incoming
              </div>
            </div>
            <div class="pa-4 pt-0">
              <v-data-table
                :headers="relTypeHeaders"
                :items="relationshipTypes"
                :items-per-page="-1"
                hide-default-footer
                @click:row="onSelectRelType"
                :item-class="getRelTypeRowClass"
                class="elevation-2 rel-type-table"
              >
                <template #[`item.direction`]="{ item }">
                  <div class="direction-cell">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-icon color="lime darken-2" v-bind="attrs" v-on="on">
                          {{
                            item.directionValue === 'out'
                              ? 'mdi-export'
                              : 'mdi-import'
                          }}
                        </v-icon>
                      </template>
                      <span>{{
                        item.directionValue === 'out' ? 'Outgoing' : 'Incoming'
                      }}</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-tab-item>

          <!-- Tab 2: Find Individual (search-driven, scoped) -->
          <v-tab-item eager>
            <!-- Selected relationship summary -->
            <div v-if="selectedConstraint" class="pa-4 pb-0">
              <strong>{{ selectedConstraint.relationship }}</strong>
              <span class="grey--text ml-2"
                >{{ selectedConstraint.directionValue === 'out' ? '→' : '←' }}
                {{ selectedConstraint.otherEnd }}</span
              >
            </div>
            <!-- Search field -->
            <div class="pa-4 pb-0">
              <v-text-field
                v-model="addSearch"
                solo
                dense
                clearable
                placeholder="Search by name, type, or ID..."
                hide-details
                prepend-inner-icon="mdi-magnify"
                :disabled="addRelationshipLoading"
                ref="individualSearch"
              ></v-text-field>
              <div class="text-caption grey--text mt-2">
                <template v-if="addRelationshipLoading">
                  Loading candidates...
                </template>
                <template v-else-if="candidateItems.length === 0 && !addSearch">
                  No unconstrained candidates remaining — all
                  {{ selectedConstraint ? selectedConstraint.otherEnd : '' }}
                  individuals already have this relationship.
                </template>
                <template v-else>
                  Showing {{ candidatePage.length }} of
                  {{ filteredCandidates.length }} candidates
                </template>
              </div>
            </div>
            <!-- Loading spinner -->
            <div
              v-if="addRelationshipLoading"
              class="d-flex flex-column align-center justify-center pa-8"
            >
              <v-progress-circular
                indeterminate
                color="lime darken-2"
                size="64"
                width="5"
              ></v-progress-circular>
              <div class="text-caption grey--text mt-3">
                Loading candidates...
              </div>
            </div>
            <!-- Empty state -->
            <div
              v-else-if="candidateItems.length === 0"
              class="d-flex flex-column align-center justify-center pa-8"
            >
              <v-icon color="grey" size="48" class="mb-3"
                >mdi-information-outline</v-icon
              >
              <div class="text-body-2 grey--text text-center">
                No unconstrained candidates remaining — all
                {{ selectedConstraint ? selectedConstraint.otherEnd : '' }}
                individuals already have this relationship.
              </div>
            </div>
            <!-- Table (only when data exists) -->
            <template v-else>
              <div class="pa-4 pt-2">
                <v-data-table
                  :headers="candidateHeaders"
                  :items="candidatePage"
                  :items-per-page="-1"
                  hide-default-footer
                  @click:row="onSelectCandidate"
                  :item-class="getCandidateRowClass"
                  class="elevation-2"
                >
                  <template #[`item.context`]="{ item }">
                    {{ item.context }}
                  </template>
                </v-data-table>
              </div>
              <!-- Pagination -->
              <div
                v-if="candidateTotalPages > 1"
                class="d-flex align-center justify-center pa-2"
              >
                <v-btn
                  icon
                  small
                  :disabled="candidatePageNum <= 1"
                  @click="candidatePageNum--"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <span class="text-caption mx-2">
                  Page {{ candidatePageNum }} of {{ candidateTotalPages }}
                </span>
                <v-btn
                  icon
                  small
                  :disabled="candidatePageNum >= candidateTotalPages"
                  @click="candidatePageNum++"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </template>
          </v-tab-item>
        </v-tabs-items>

        <v-card-actions class="flex-shrink-0">
          <v-btn @click="onAdd" :disabled="noSelection" text color="secondary"
            >Add {{ newRelationship }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// TODO this new implementation makes the data properties componewnt redundant.
// The decision on what subset of data properties to show where is taken here.

// TODO the undo button shall be in effect only when real changes has happened so a method to compare two individuals for quality is needed...

import humanizeString from 'humanize-string'

import IriValue from '@/components/IriValue.vue'
import DataProp from '@/components/DataProperty.vue'
// TODO remove when component section have been updated...
import DataProperties from '@/components/DataProperties.vue'
import ObjectProp from '@/components/ObjectProperty.vue'
// TODO add guard for reading individual
import {
  DataProperty,
  ObjectProperty,
  Iris,
  Literal,
  JsonLiteral,
} from '@/rec-cache/rec-cache-types'
import { Edge } from '@/rec-cache/rec-cache-edge'
import { recIcon } from '@/vue-wob-cache/vue-wob-icons'
import { RecIndividual } from '@/rec-cache/rec-cache-individual'
import { WobAggregators } from '@/vue-wob-cache/vue-wob-aggregators'
import { Alarms, ExceptionEvent } from '@/vue-wob-cache/vue-wob-alarms'

const MetaIris = [
  'http://purl.org/dc/terms/created',
  'http://purl.org/dc/terms/modified',
]

const genericProps = [Iris.recName, Iris.recIdentifiers]

// Cache for resolveOntologyType lookups (type IRI value → resolved Iri or null).
const resolveCache = new Map()

// Asset properties - applicable to Asset and all its subclasses
// Based on DTDL Asset interface data properties
const assetProps = [
  Iris.recIPAddress,
  Iris.recMACAddress,
  Iris.recAssetTag,
  Iris.recCommissioningDate,
  Iris.recInitialCost,
  Iris.recInstallationDate,
  Iris.recModelNumber,
  Iris.recSerialNumber,
  Iris.recTurnoverDate,
  Iris.recWeight,
]

// Property datatype registry - maps property IRI to XSD datatype
// This ensures new properties are created with the correct datatype
const propertyDatatypes = {
  [Iris.recCommissioningDate]: Iris.xsdDate,
  [Iris.recInstallationDate]: Iris.xsdDate,
  [Iris.recTurnoverDate]: Iris.xsdDate,
  [Iris.recIdentifiers]: Iris.rdfJSON,
  // String properties (IPAddress, MACAddress, assetTag, initialCost, modelNumber,
  // serialNumber, weight) default to xsd:string via Literal constructor
}

// Map of property id strings to arrays of property Iris
const specificProps = {}

export default {
  components: { IriValue, DataProp, DataProperties, ObjectProp },
  props: {
    individual: {
      type: Object,
      default: () => new RecIndividual(),
    },
    edit: {
      type: Boolean,
      default: false,
    },
    link: {
      type: Boolean,
      default: false,
    },
  },
  mounted: function () {
    console.log('components.individual-expanded.mounted', this.individual)
    console.log('JSONLD', this.individual.toJSONLD())
    this.initExpandedPanels()
  },
  watch: {
    individual: function () {
      this.initExpandedPanels()
    },
    addSearch: function () {
      this.candidatePageNum = 1
    },
  },
  data: () => ({
    showAdd: false,
    addTab: 0,
    addSearch: '',
    expandedComponentPanels: [],
    // Tab 1: Relationship type selection
    relTypeHeaders: [
      { text: '', value: 'direction', width: '40px', sortable: false },
      { text: 'Relationship', value: 'relationship', width: '180px' },
      { text: 'Other End', value: 'otherEnd' },
    ],
    selectedConstraint: null,
    // Tab 2: Individual search/selection
    candidateHeaders: [
      { text: 'Type', value: 'targetType' },
      { text: 'Individual', value: 'individual' },
      { text: 'Real Estate', value: 'context' },
    ],
    candidateItems: [], // all candidates for selected constraint
    addSelection: {},
    selectedRowKey: null,
    candidatePageNum: 1,
    candidatePageSize: 50,
    addedEdges: [],
    removedEdges: [],
    addRelationshipLoading: false,
  }),
  computed: {
    random: function () {
      return Math.random().toString(36).slice(2, 8)
    },
    metaProperties: function () {
      return this.individual.properties.filter(
        (p) => p instanceof DataProperty && MetaIris.includes(p.name.value)
      )
    },
    dataProperties: function () {
      return this.individual.properties.filter(
        (p) => p instanceof DataProperty && !MetaIris.includes(p.name.value)
      )
    },
    objectProperties: function () {
      return this.individual.properties
        .filter((p) => p instanceof ObjectProperty)
        .filter((p) => !WobAggregators.includes(p.name.value))
    },
    inverseObjectProperties: function () {
      const edges = this.$wob
        .queryEdges({ target: this.individual.id.value })
        .concat(
          this.addedEdges.filter((e) => e.target.equals(this.individual.id))
        )
        .filter((edge) => !this.removedEdges.find((re) => re.equals(edge)))
      const properties = []
      const labels = []
      // TODO perhaps use set for labels instead...
      edges.forEach((e) => {
        if (!labels.includes(e.label.value)) {
          labels.push(e.label.value)
          properties.push(new ObjectProperty(e.label))
        }
        properties.find((p) => p.name.equals(e.label.value))?.addValue(e.source)
      })
      return properties.filter((p) => !WobAggregators.includes(p.name.value))
    },
    recTypeIcon: function () {
      return recIcon(this.individual.type, this.hasAlert(this.individual))
    },
    isAssetOrSubclass: function () {
      const superclasses = this.$wobo.superclasses(this.individual.type) || []
      return (
        this.individual.type.equals(Iris.recAsset) ||
        superclasses.find((sc) => sc.equals(Iris.recAsset))
      )
    },
    allowedProps: function () {
      const specific = specificProps[this.individual.type.value] || []
      const asset = this.isAssetOrSubclass ? assetProps : []
      return specific.concat(genericProps).concat(asset)
    },
    availableProps: function () {
      return this.allowedProps.filter(
        (p) => !this.individual.dataPropNames.find((n) => n.value == p)
      )
    },
    noAvailableProps: function () {
      return this.availableProps.length == 0
    },
    disallowedProps: function () {
      return this.allowedProps.filter((p) =>
        this.individual.dataPropNames.find((n) => n.value == p)
      )
    },
    addDataPropMenu: function () {
      const genericIris = genericProps.map((p) => p)

      // Separate available and disallowed into generic and specific
      const availableGeneric = []
      const availableSpecific = []
      const disallowedGeneric = []
      const disallowedSpecific = []

      this.availableProps.forEach((p) => {
        const item = {
          iri: this.$wob.owl.iri(p),
          label: humanizeString(this.$wob.owl.iri(p).short),
          disabled: false,
        }
        if (genericIris.includes(p)) {
          availableGeneric.push(item)
        } else {
          availableSpecific.push(item)
        }
      })

      this.disallowedProps.forEach((p) => {
        const item = {
          iri: this.$wob.owl.iri(p),
          label: humanizeString(this.$wob.owl.iri(p).short),
          disabled: true,
        }
        if (genericIris.includes(p)) {
          disallowedGeneric.push(item)
        } else {
          disallowedSpecific.push(item)
        }
      })

      // Sort specific props alphabetically by label
      availableSpecific.sort((a, b) => a.label.localeCompare(b.label))
      disallowedSpecific.sort((a, b) => a.label.localeCompare(b.label))

      // Build menu: generic props, divider, specific props (alphabetically)
      const menu = [...availableGeneric, ...disallowedGeneric]

      // Add divider if we have both generic and specific props
      if (
        menu.length > 0 &&
        (availableSpecific.length > 0 || disallowedSpecific.length > 0)
      ) {
        menu.push({ divider: true })
      }

      menu.push(...availableSpecific, ...disallowedSpecific)

      return menu
    },
    newRelationship: function () {
      return (
        (this.addSelection.relationship?.short || '') +
        ' ' +
        (this.addSelection.label?.value || '')
      )
    },
    noSelection: function () {
      return Object.keys(this.addSelection).length === 0
    },
    // Tab 1: Build relationship type rows from constraints (instant, no graph traversal)
    relationshipTypes: function () {
      const type = this.individual.type
      const items = []

      // Outgoing: this individual is a source
      let outConstraints = this.$constraints.inSources(type)
      if (outConstraints.length === 0) {
        const resolved = this.resolveOntologyType(type)
        if (resolved) outConstraints = this.$constraints.inSources(resolved)
      }
      outConstraints.forEach((constraint) => {
        items.push({
          direction: '',
          directionValue: 'out',
          relationship: constraint.relationship.short,
          otherEnd: constraint.targets.map((t) => t.short).join(', '),
          constraint: constraint,
        })
      })

      // Incoming: this individual is a target
      let inConstraints = this.$constraints.inTargets(type)
      if (inConstraints.length === 0) {
        const resolved = this.resolveOntologyType(type)
        if (resolved) inConstraints = this.$constraints.inTargets(resolved)
      }
      inConstraints.forEach((constraint) => {
        items.push({
          direction: '',
          directionValue: 'in',
          relationship: constraint.relationship.short,
          otherEnd: constraint.sources.map((t) => t.short).join(', '),
          constraint: constraint,
        })
      })

      return items
    },
    // Tab 2: Filter candidates by search text
    filteredCandidates: function () {
      if (!this.addSearch || this.addSearch.trim() === '') {
        return this.candidateItems
      }
      const searchTerms = this.addSearch.toLowerCase().trim().split(/\s+/)
      return this.candidateItems.filter((item) => {
        const searchableText = [item.targetType, item.individual, item.context]
          .join(' ')
          .toLowerCase()
        return searchTerms.every((term) => searchableText.includes(term))
      })
    },
    candidateTotalPages: function () {
      return Math.max(
        1,
        Math.ceil(this.filteredCandidates.length / this.candidatePageSize)
      )
    },
    candidatePage: function () {
      const start = (this.candidatePageNum - 1) * this.candidatePageSize
      return this.filteredCandidates.slice(
        start,
        start + this.candidatePageSize
      )
    },

    // TODO seems not to calculate correctly...
    // newRelationshipSelected: function () {
    //   return !!this.addSelection.relationship
    // },
    // TODO remove when new implementation has been tested...
    // hasException: function () {
    //   return this.$wob.exceptions.ids.includes(this.individual.id.value)
    // },

    // hasComponent: function () {
    //   return !!this.$wob.hasComponents(this.individual.id)
    // },
  },
  methods: {
    initExpandedPanels: function () {
      // Auto-expand non-empty components
      const panels = []
      if (this.individual && this.individual.components) {
        this.individual.components.forEach((component, index) => {
          if (!this.isComponentEmpty(component)) {
            panels.push(index)
          }
        })
      }
      this.expandedComponentPanels = panels
    },
    componentTypeIcon: function (type) {
      return recIcon(type)
    },
    isComponentEmpty: function (component) {
      // A component is "empty" if it has no data properties beyond meta (modified/created)
      const nonMetaProps = component.properties.filter(
        (p) => p instanceof DataProperty && !MetaIris.includes(p.name.value)
      )
      return nonMetaProps.length === 0
    },
    hasAlert: function (individual) {
      const id = individual.id.value
      return (
        this.$wob.openExceptions.ids.map((id) => id.value).includes(id) ||
        this.$wob.activeAlarms.ids.map((id) => id.value).includes(id) ||
        this.$wob.alarmingAssets.ids.map((id) => id.value).includes(id)
      )
    },
    hasWarning: function (individual) {
      return this.$wob.impactedAssets.ids
        .map((id) => id.value)
        .includes(individual.id.value)
    },

    isAlarmOrException: function (individual) {
      return (
        Alarms.includes(individual.type?.value) ||
        ExceptionEvent == individual.type?.value
      )
    },
    // TODO substitued by other methods, remove after testing...
    // componentException: function (individual) {
    //   return (
    //     this.$wob.exceptions.ids.includes(individual.id.value) ||
    //     this.$wob.impacts.ids.includes(individual.id.value)
    //   )
    // },
    onClick: function (event) {
      console.log('components.individual-expanded.methods.on-click', event)
      const apartmentViewTriggers = [
        'https://w3id.org/rec#Apartment',
        'https://webofbuildings.com/ontology/LmApartment',
        'https://webofbuildings.com/ontology/ResidentialApartment',
      ]
      if (apartmentViewTriggers.includes(this.individual.type.value)) {
        // Route change to apartment view.
        event.stopPropagation()
        this.$router.push(
          `/apartment/${encodeURIComponent(this.individual.id.value)}`
        )
      } else if (this.link) {
        // Navigate to individual view (e.g. from tree view).
        event.stopPropagation()
        this.$router.push(
          `/individual/${encodeURIComponent(this.individual.id.value)}`
        )
      }
    },
    onDeleteProp: function (property) {
      this.individual.removeProperty(property)
    },
    addProperty: function (iri) {
      // Look up the datatype for this property from the registry
      const datatype = propertyDatatypes[iri.value]

      let value
      if (datatype === Iris.rdfJSON) {
        // JSON literals use JsonLiteral class
        value = new JsonLiteral({})
      } else {
        // All other datatypes (including dateTime) use Literal class
        // Defaults to xsd:string if not in registry
        value = new Literal('', datatype || Iris.xsdString)
      }

      this.individual.addProperties(new DataProperty(iri, value))
    },
    // Parameter target is an Iri.
    onDeleteRel: function (property, target) {
      console.log('ON DEL REL', property, target)

      if (property.isOne) {
        this.individual.removeProperty(property)
      } else {
        property.removeValue(target)
      }

      // Add to removed edges if not an previously added edge.
      const inAdded = this.addedEdges.find(
        (edge) => edge.label.equals(property.name) && edge.target.equals(target)
      )

      if (inAdded) {
        this.addedEdges = this.addedEdges.filter(
          (edge) => !edge.equals(inAdded)
        )
      } else {
        this.removedEdges.push(
          new Edge(this.individual.id, target, property.name)
        )
      }
      console.log('ON DEL REL IN ADDED/REMOVED', inAdded, this.removedEdges)
    },
    // Resolves a type to its nearest known ancestor in the reduced ontology.
    // When an individual has a type not present in the ontology (e.g.
    // brick:Chilled_Water_Supply_Temperature_Sensor), this walks the Brick-style
    // underscore-delimited name to find the first known parent
    // (e.g. Temperature_Sensor → Sensor → Point).
    resolveOntologyType: function (type) {
      if (this.$wobo.superclasses(type) !== undefined) return type

      // Check cache first.
      if (resolveCache.has(type.value)) return resolveCache.get(type.value)

      // Extract namespace and local name from the full IRI.
      const value = type.value
      const hashIdx = value.lastIndexOf('#')
      const slashIdx = value.lastIndexOf('/')
      const splitIdx = Math.max(hashIdx, slashIdx)
      if (splitIdx < 0) {
        resolveCache.set(value, null)
        return null
      }

      const namespace = value.substring(0, splitIdx + 1)
      const localName = value.substring(splitIdx + 1)
      const segments = localName.split('_')

      // Progressively remove leading segments to find a known type.
      for (let i = 1; i < segments.length; i++) {
        const candidateName = segments.slice(i).join('_')
        const candidateIri = this.$wob.owl.iri(namespace + candidateName)
        if (this.$wobo.superclasses(candidateIri) !== undefined) {
          console.log(
            'RESOLVED ONTOLOGY TYPE',
            type.value,
            '→',
            candidateIri.value
          )
          resolveCache.set(value, candidateIri)
          return candidateIri
        }
      }
      resolveCache.set(value, null)
      return null
    },

    // Expands ontology types (from constraint sources/targets) into actual
    // individual IDs from the typeMap. Includes types not in the reduced ontology
    // by checking all typeMap keys and resolving them to their nearest known ancestor.
    expandTypesToIndividuals: function (ontologyTypes) {
      // Get subclasses for each ontology type.
      const knownTypes = ontologyTypes
        .map((t) => this.$wobo.subclasses(t).concat(t))
        .reduce((prev, curr) => prev.concat(curr), [])

      const knownTypeValues = new Set(knownTypes.map((t) => t.value))

      // Look up individuals for known types.
      let ids = knownTypes
        .map((sc) => this.$wob.typeSelector.typeMap.get(sc.value))
        .filter((idSet) => !!idSet)
        .map((idSet) => [...idSet])
        .reduce((prev, curr) => prev.concat(curr), [])

      // Also check typeMap for types NOT in the ontology that resolve to a known type.
      for (const [typeKey, idSet] of this.$wob.typeSelector.typeMap.entries()) {
        if (knownTypeValues.has(typeKey)) continue // Already included.
        const resolved = this.resolveOntologyType(this.$wob.owl.iri(typeKey))
        if (resolved && knownTypeValues.has(resolved.value)) {
          ids = ids.concat([...idSet])
        }
      }

      // Deduplicate individual IDs.
      return [...new Set(ids)]
    },

    // Finds the owning RealEstate for a target individual by traversing upward
    // through hierarchy relationships. Returns the RealEstate label or empty string.
    resolveRealEstate: function (targetId) {
      const visited = new Set()
      const upLabels = [
        'https://w3id.org/rec#isPartOf',
        'https://w3id.org/rec#locatedIn',
      ]
      const downLabels = [
        'https://w3id.org/rec#includes',
        'https://w3id.org/rec#hasPart',
        'https://w3id.org/rec#isLocationOf',
      ]
      const realEstateType = 'https://w3id.org/rec#RealEstate'

      const traverse = (idValue, depth) => {
        if (depth > 6 || visited.has(idValue)) return null
        visited.add(idValue)

        const upEdges = this.$wob
          .queryEdges({ source: idValue })
          .filter((e) => upLabels.includes(e.label.value))
        const downEdges = this.$wob
          .queryEdges({ target: idValue })
          .filter((e) => downLabels.includes(e.label.value))

        const parentIris = upEdges
          .map((e) => e.target)
          .concat(downEdges.map((e) => e.source))

        for (const parentIri of parentIris) {
          const parent = this.$wob.read(parentIri)
          if (!parent) continue
          // Check if this parent is a RealEstate (or subclass thereof).
          const parentType = parent.type?.value
          if (parentType) {
            const supers = this.$wobo.superclasses(parent.type)
            const isRealEstate =
              parentType === realEstateType ||
              (supers && supers.some((s) => s.value === realEstateType))
            if (isRealEstate) {
              return parent.label?.value || parent.id?.short || ''
            }
          }
          const found = traverse(parentIri.value, depth + 1)
          if (found) return found
        }
        return null
      }

      return traverse(targetId.value, 0) || ''
    },

    // Opens the Add Relationship dialog — instant, no heavy computation.
    onAddRelationship: function () {
      console.log('ON ADD REL')
      this.showAdd = true
      this.addTab = 0
      this.selectedConstraint = null
      this.addSelection = {}
      this.selectedRowKey = null
      this.addSearch = ''
      this.candidateItems = []
      this.candidatePageNum = 1
      this.addRelationshipLoading = false
    },

    // Tab 1: Row class for selected relationship type
    getRelTypeRowClass: function (item) {
      if (!this.selectedConstraint) return ''
      const key = `${item.directionValue}|${item.relationship}|${item.otherEnd}`
      const selKey = `${this.selectedConstraint.directionValue}|${this.selectedConstraint.relationship}|${this.selectedConstraint.otherEnd}`
      return key === selKey ? 'selected-row' : ''
    },

    // Tab 1: User clicks a relationship type row → switch to Tab 2 and load candidates
    onSelectRelType: async function (row) {
      this.selectedConstraint = row
      this.addSelection = {}
      this.selectedRowKey = null
      this.addSearch = ''
      this.candidatePageNum = 1
      this.addTab = 1
      this.addRelationshipLoading = true
      this.candidateItems = []

      // Yield to UI so tab switch and loading indicator render
      await new Promise((resolve) => setTimeout(resolve, 50))

      const constraint = row.constraint
      const direction = row.directionValue

      // Expand types to individuals — scoped to this single constraint
      const typesToExpand =
        direction === 'out' ? constraint.targets : constraint.sources
      const individualIds = this.expandTypesToIndividuals(typesToExpand)

      await new Promise((resolve) => setTimeout(resolve, 0))

      // Apply degree constraints
      let possibilities
      if (direction === 'out') {
        possibilities = this.filterOutgoingCandidates(constraint, individualIds)
      } else {
        possibilities = this.filterIncomingCandidates(constraint, individualIds)
      }

      await new Promise((resolve) => setTimeout(resolve, 0))

      // Build display items in batches
      const BATCH_SIZE = 100
      const items = []

      for (let i = 0; i < possibilities.length; i += BATCH_SIZE) {
        const batch = possibilities.slice(i, i + BATCH_SIZE)
        batch.forEach((idStr) => {
          const id = this.$wob.owl.iri(idStr)
          const targetIndividual = this.$wob.read(id)
          const context = this.resolveRealEstate(id)
          items.push({
            targetType: targetIndividual?.type?.short || 'Unknown',
            individual: targetIndividual?.label?.value || id.short,
            context: context,
            meta: {
              relationship: constraint.relationship,
              target: id,
              label: targetIndividual?.label,
            },
          })
        })
        if (i + BATCH_SIZE < possibilities.length) {
          await new Promise((resolve) => setTimeout(resolve, 0))
        }
      }

      // Deduplicate
      const seen = new Set()
      const uniqueItems = items.filter((item) => {
        const iriKey = item.meta.target.value
        const displayKey = `${item.targetType}|${item.individual}|${item.context}`
        if (seen.has(iriKey) || seen.has(displayKey)) return false
        seen.add(iriKey)
        seen.add(displayKey)
        return true
      })

      this.candidateItems = uniqueItems
      this.addRelationshipLoading = false
      this.candidatePageNum = 1
    },

    // Filter outgoing candidates by degree constraints
    filterOutgoingCandidates: function (constraint, targetIds) {
      const edges = targetIds
        .map((t) =>
          this.$wob.queryEdges({
            label: constraint.relationship.value,
            target: t,
          })
        )
        .reduce((prev, curr) => prev.concat(curr), [])
        .concat(this.addedEdges)
        .filter((edge) => !this.removedEdges.find((re) => re.equals(edge)))

      const cardinality = new Map()
      edges.forEach((edge) => {
        const target = cardinality.get(edge.target.value)
        cardinality.set(edge.target.value, target ? target + 1 : 1)
      })

      const constrained = [...cardinality.entries()]
        .filter(([, inDegree]) => inDegree >= (constraint.inDegree || 1000000))
        .map(([target]) => target)

      return targetIds.filter((t) => !constrained.includes(t))
    },

    // Filter incoming candidates by degree constraints
    filterIncomingCandidates: function (constraint, sourceIds) {
      // Check in-degree constraint for this individual as a target
      const inEdges = this.$wob
        .queryEdges({
          label: constraint.relationship.value,
          target: this.individual.id.value,
        })
        .concat(
          this.addedEdges.filter(
            (e) =>
              e.label.equals(constraint.relationship) &&
              e.target.equals(this.individual.id)
          )
        )
        .filter((edge) => !this.removedEdges.find((re) => re.equals(edge)))

      // If in-degree already met, no candidates possible
      if (inEdges.length >= (constraint.inDegree || 1000000)) return []

      // Check out-degree constraint for each potential source
      return sourceIds.filter((s) => {
        if (!constraint.outDegree) return true
        const outEdges = this.$wob.queryEdges({
          source: s,
          label: constraint.relationship.value,
        })
        return outEdges.length < constraint.outDegree
      })
    },

    // Tab 2: Row class for selected candidate
    getCandidateRowClass: function (item) {
      const key = item.meta.target.value
      return key === this.selectedRowKey ? 'selected-row' : ''
    },

    // Tab 2: User clicks a candidate row
    onSelectCandidate: function (row) {
      const key = row.meta.target.value
      if (key === this.selectedRowKey) {
        this.selectedRowKey = null
        this.addSelection = {}
      } else {
        this.selectedRowKey = key
        this.addSelection = {
          ...row.meta,
          direction: this.selectedConstraint.directionValue,
        }
      }
    },
    onAdd: function () {
      console.log('ON ADD', this.addSelection)
      const direction = this.addSelection.direction

      if (direction === 'in') {
        // Incoming: the selected individual becomes the source, this individual is the target.
        // Emit event so the parent (IndividualView) can persist the source individual on save.
        this.$emit('add-incoming', {
          source: this.addSelection.target,
          relationship: this.addSelection.relationship,
          target: this.individual.id,
        })
      } else {
        // Outgoing: this individual is the source, selected individual is the target.
        this.individual.addProperties(
          new ObjectProperty(
            this.addSelection.relationship,
            this.addSelection.target
          )
        )
      }
      this.showAdd = false

      // Keeping track of added edges for degree constraint calculations.
      const edgeSource =
        direction === 'in' ? this.addSelection.target : this.individual.id
      const edgeTarget =
        direction === 'in' ? this.individual.id : this.addSelection.target

      const inRemoved = this.removedEdges.find(
        (edge) =>
          edge.label.equals(this.addSelection.relationship) &&
          edge.source.equals(edgeSource) &&
          edge.target.equals(edgeTarget)
      )
      if (inRemoved) {
        this.removedEdges = this.removedEdges.filter(
          (edge) => !edge.equals(inRemoved)
        )
      } else {
        this.addedEdges.push(
          new Edge(edgeSource, edgeTarget, this.addSelection.relationship)
        )
      }
      this.addSelection = {}
      this.selectedRowKey = null
    },
  },
  // beforeRouteUpdate: function (to, from, next) {
  //   console.log('components.individual-view.before-route-update', to)
  //   this.individual = this.$wob.read(new Iri(to.params.id))
  //   next()
  // },

  // mounted: function () {
  //   console.log('views.individual-view.mounted', this.id)
  //   this.individual = this.$wob.read(new Iri(this.id))
  //   console.log('views.individual-view.mounted.individual', this.individual)
  // },
}
</script>

<style scoped>
/* Clickable table rows */
::v-deep .rel-type-table tr,
::v-deep .v-data-table tr {
  cursor: pointer;
}

/* Force table to maintain consistent column widths */
::v-deep .v-data-table {
  width: 100%;
}

::v-deep .v-data-table table {
  width: 100%;
}

/* Tab 1 table: fixed layout for direction + relationship columns */
::v-deep .rel-type-table table {
  table-layout: fixed;
}

::v-deep .v-data-table th,
::v-deep .v-data-table td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Direction icon column: only applies to Tab 1 (rel-type-table) */
::v-deep .rel-type-table td:first-child,
::v-deep .rel-type-table th:first-child {
  overflow: visible;
  text-overflow: clip;
  text-align: center;
  padding: 0 !important;
  width: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
}

.direction-cell {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

/* Highlight the selected row in the add-relationship table */
::v-deep .v-data-table tr.selected-row {
  background-color: rgba(25, 118, 210, 0.12) !important;
}

::v-deep .v-data-table tr.selected-row:hover {
  background-color: rgba(25, 118, 210, 0.2) !important;
}

/* Component expansion panels: match v-sheet appearance */
.component-panels {
  gap: 16px;
  display: flex;
  flex-direction: column;
}

.component-panel {
  background-color: white !important;
}

.component-panel::before {
  box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14),
    0 1px 3px 0 rgba(0, 0, 0, 0.12) !important;
}

/* Remove default expansion panel border */
.component-panels ::v-deep .v-expansion-panel::after {
  border: none !important;
}

/* Rotate chevron: right (>) when collapsed, up (^) when expanded */
.component-panel ::v-deep .v-expansion-panel-header__icon .v-icon {
  transform: rotate(-90deg);
}

.component-panel
  ::v-deep
  .v-expansion-panel--active
  > .v-expansion-panel-header--active
  .v-expansion-panel-header__icon:not(.v-expansion-panel-header__icon--disable-rotate)
  .v-icon {
  transform: rotate(-180deg);
}

/* Remove extra padding from panel content wrapper */
.component-panel ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 16px 16px;
}
</style>
